import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ReportReason, UserDoc } from '@astrally/shared';
import { COMPATIBILITY_DIMENSIONS } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { initialsOf, ScreenHeader, Section } from '../../components/common';
import type { CompatibilityView } from '../../lib/backend/types';

const DIM_LABEL: Record<string, string> = {
  // Listed first because COMPATIBILITY_DIMENSIONS puts the kundli match first —
  // it is the headline dimension, not one signal among the rest.
  kundli: 'Kundli match',
  emotional: 'Emotional',
  communication: 'Communication',
  chemistry: 'Chemistry',
  stability: 'Stability',
  longTerm: 'Long-term',
  friendship: 'Friendship',
  professional: 'Professional',
};

/** Full profile + deterministic compatibility breakdown. */
export function ProfileView() {
  const { targetUid } = useParams<{ targetUid: string }>();
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDoc | null>(null);
  const [compat, setCompat] = useState<CompatibilityView | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [gone, setGone] = useState(false);
  const [saved, setSaved] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);

  useEffect(() => {
    if (!targetUid) return;
    // Router reuses this component when only the param changes, so the previous
    // person's data — and their "gone" verdict — must not survive the switch.
    let live = true;
    setUser(null);
    setCompat(null);
    setGone(false);
    setSaved(false);
    setActionFailed(false);
    app.backend
      .getUser(targetUid)
      .then((u) => {
        if (!live) return;
        // A deleted, blocked or hidden profile resolves to null. Treating that
        // as "still loading" spun the placeholder forever with no way out.
        if (u) setUser(u);
        else setGone(true);
      })
      .catch((e) => {
        console.warn(e);
        if (live) setGone(true);
      });
    app.backend
      .compatibilityGet(targetUid)
      .then((v) => live && setCompat(v))
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUid]);

  if (!targetUid || gone) {
    return (
      <div className="screen">
        <ScreenHeader title={t('discover.view')} back />
        <div className="banner banner--danger small" style={{ marginTop: 8 }} role="status">
          {t('profile.gone')}
        </div>
        <button
          className="btn btn--primary btn--block"
          style={{ marginTop: 12 }}
          onClick={() => navigate('/discover')}
        >
          {t('discover.title')}
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="screen">
        <ScreenHeader title={t('discover.view')} back />
        <div className="center" style={{ marginTop: 40 }}><div className="spinner" /></div>
      </div>
    );
  }

  const quota = app.entitlement?.quota;

  const connect = async () => {
    // Gate BEFORE sending: an exhausted allowance opens the Pro screen and
    // the request is never attempted.
    if (quota && quota.remaining <= 0) {
      navigate('/profile/subscription');
      return;
    }
    try {
      await app.backend.connectionRequest(targetUid);
      await app.refreshEntitlement();
      alert('Request sent — they can accept or decline privately.');
      navigate('/discover');
    } catch (e) {
      const raw = String(e);
      if (raw.includes('quota-exhausted-base-lifetime')) navigate('/profile/subscription');
      else alert(raw.slice(0, 180));
    }
  };

  /** Fire-and-forget previously: the star never moved and a rejection vanished. */
  const save = async () => {
    if (saved) return;
    setActionFailed(false);
    setSaved(true);
    try {
      await app.backend.discoveryAction(targetUid, 'save');
    } catch (e) {
      console.warn(e);
      setSaved(false);
      setActionFailed(true);
    }
  };

  const block = async () => {
    if (!confirm(`Block ${user.displayName}? They will not be told.`)) return;
    await app.backend.blockUser(targetUid);
    await app.refreshSocial();
    navigate('/discover');
  };

  const report = async (reason: ReportReason) => {
    await app.backend.reportUser(targetUid, reason, `profile:${targetUid}`);
    setReportOpen(false);
    alert('Thank you. Our safety team will review this.');
  };

  const c = compat?.compatibility;

  return (
    <div className="screen">
      <ScreenHeader title={t('discover.view')} back />

      <div className="card row" style={{ marginBottom: 10 }}>
        <div className="avatar avatar--gold" style={{ width: 60, height: 60 }}>
          {initialsOf(user.displayName)}
        </div>
        <div style={{ flex: 1 }}>
          <div className="h2">
            {user.displayName}
            {user.age ? <span className="muted">, {user.age}</span> : null}
            {user.verification?.photo && <span className="tiny gold"> ✓</span>}
          </div>
          <div className="tiny muted">
            {user.city}
            {user.occupation ? ` · ${user.occupation}` : ''}
            {' · '}
            {(user.primaryIntention ?? user.intents[0] ?? '').replace(/-/g, ' ')}
          </div>
          <div className="tiny faint">{user.languages.join(', ')}</div>
        </div>
        {c && <span className="badge badge--B" style={{ fontSize: '1.05rem' }}>{c.overall}</span>}
      </div>

      {user.bio && <div className="card small muted" style={{ marginBottom: 10 }}>{user.bio}</div>}

      {user.interests.length > 0 && (
        <div className="wrap" style={{ marginBottom: 12 }}>
          {user.interests.map((i) => (
            <span key={i} className="chip" style={{ cursor: 'default' }}>{i}</span>
          ))}
        </div>
      )}

      {c && (
        <Section title={t('discover.breakdown')} term="compatibility-score">
          <div className="card col" style={{ gap: 8 }}>
            {compat?.full && c.dimensions ? (
              COMPATIBILITY_DIMENSIONS.map((d) => (
                <div key={d}>
                  <div className="spread tiny">
                    <span className="muted">{DIM_LABEL[d]}</span>
                    <span className="gold">{c.dimensions![d]}</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-2)' }}>
                    <div
                      style={{
                        width: `${c.dimensions![d]}%`,
                        height: '100%',
                        borderRadius: 3,
                        background:
                          c.dimensions![d] >= 70
                            ? 'var(--ok)'
                            : c.dimensions![d] >= 50
                              ? 'var(--accent)'
                              : 'var(--gold)',
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <>
                {(c.highlights ?? []).map((h) => (
                  <div key={h} className="small">✓ {h}</div>
                ))}
                <div className="banner banner--info small">
                  {compat?.upgradeHint ?? t('discover.upgradeFull')}{' '}
                  <Link to="/profile/subscription">{t('pro.upgrade')} →</Link>
                </div>
              </>
            )}
            <div className="tiny faint">
              Deterministic score {c.scoreVersion} — a starting point for conversation, never proof
              a person is right or safe for you.
            </div>
          </div>
        </Section>
      )}

      {compat?.full && (c?.highlights?.length || c?.frictions?.length) ? (
        <div className="row" style={{ alignItems: 'stretch', marginBottom: 12 }}>
          {c?.highlights && c.highlights.length > 0 && (
            <div className="card col" style={{ flex: 1, gap: 4 }}>
              <div className="tiny gold">{t('discover.whyWork')}</div>
              {c.highlights.map((h) => (
                <div key={h} className="tiny">{h}</div>
              ))}
            </div>
          )}
          {c?.frictions && c.frictions.length > 0 && (
            <div className="card col" style={{ flex: 1, gap: 4 }}>
              <div className="tiny danger">{t('discover.friction')}</div>
              {c.frictions.map((f) => (
                <div key={f} className="tiny muted">{f}</div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {quota && quota.kind === 'base-lifetime' && (
        <div className="banner banner--info small" style={{ marginBottom: 10 }}>
          {quota.remaining} of {app.entitlement?.config.base.lifetimeOutgoingRequests}{' '}
          {t('discover.remaining')}
        </div>
      )}

      {actionFailed && (
        <div className="banner banner--danger small" style={{ marginBottom: 10 }} role="status">
          {t('common.error')}
        </div>
      )}

      <div className="row" style={{ marginBottom: 10 }}>
        <button className="btn btn--primary" style={{ flex: 2 }} onClick={connect}>
          {t('discover.connect')}
        </button>
        <button
          className="btn btn--ghost"
          style={{ flex: 1 }}
          aria-label={t('discover.save')}
          aria-pressed={saved}
          onClick={save}
        >
          <span aria-hidden>{saved ? '★' : '☆'}</span> {t('discover.save')}
        </button>
      </div>
      <div className="row">
        <button className="btn btn--ghost btn--small" style={{ flex: 1 }} onClick={block}>
          {t('msg.block')}
        </button>
        <button
          className="btn btn--ghost btn--small danger"
          style={{ flex: 1 }}
          onClick={() => setReportOpen(!reportOpen)}
        >
          {t('msg.report')}
        </button>
      </div>
      {reportOpen && (
        <div className="wrap" style={{ marginTop: 8 }}>
          {(['fake-profile', 'harassment', 'inappropriate-content', 'safety-concern', 'other'] as ReportReason[]).map((r) => (
            <button key={r} className="chip" onClick={() => report(r)}>
              {r.replace(/-/g, ' ')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
